import {
  AddressType,
  UserAccountType,
  BusinessDataType,
  BusinessAddressType,
  SingleUserProfileType,
} from '@am3nd-org/pointcraft-types';

import { type Employee } from '../schemas/employee';
import { BusinessProfile } from '../types/settings';

/**
 * Maps BusinessAddressType to AddressType
 * BusinessAddressType may have slightly different structure
 */
export function mapBusinessAddressToAddress(address: BusinessAddressType): AddressType {
  return {
    street1: address.street1 || '',
    street2: address.street2 || '',
    city: address.city,
    state: address.state,
    postalCode: address.postalCode || '',
    country: address.country,
  };
}

/**
 * Maps a role string from the user profile to UserAccountType
 */
function mapRoleToUserAccountType(role?: string): UserAccountType {
  if (!role) return UserAccountType.BUSINESS_VIEWER;

  const normalizedRole = role.toUpperCase();

  // Check if it's already a valid UserAccountType value
  if (Object.values(UserAccountType).includes(normalizedRole as UserAccountType)) {
    return normalizedRole as UserAccountType;
  }

  // Legacy mapping for old role strings
  if (normalizedRole === 'ADMIN') return UserAccountType.BUSINESS_ADMIN;
  if (normalizedRole === 'MANAGER') return UserAccountType.BUSINESS_MANAGER;
  if (normalizedRole === 'VIEWER') return UserAccountType.BUSINESS_VIEWER;

  return UserAccountType.BUSINESS_VIEWER;
}

/**
 * Maps SingleUserProfileType (from useUserDataStore) to Employee (settings type)
 */
export function mapSingleUserToEmployee(user: SingleUserProfileType): Employee {
  return {
    id: user.userId,
    name: user.name,
    email: user.email,
    phone: user.phone,
    role: mapRoleToUserAccountType(user.role),
    status: user.isActive ? 'active' : 'inactive',
    permissions: (user.permissions || []).map((p) => String(p)),
    address: user.address, // Address is now AddressType directly
    joinedDate: new Date(), // Default to current date since user profile doesn't have this
  };
}

/**
 * Maps an array of SingleUserProfileType to Employee array
 */
export function mapUsersToEmployees(users: SingleUserProfileType[]): Employee[] {
  return users.map(mapSingleUserToEmployee);
}

/**
 * Maps BusinessDataType (from useUserDataStore) to BusinessProfile (settings type)
 */
export function mapBusinessToBusinessProfile(business: BusinessDataType): BusinessProfile {
  return {
    companyName: business.name,
    email: business.email || '',
    phone: business.phone,
    address: mapBusinessAddressToAddress(business.address),
  };
}
